import type { Campaign, Dimension } from '@/types/config'

export const campaignHasTestsRunning = (campaign: Campaign) => {
  return (
    campaign.is_active &&
    campaign.goal_ids.length > 0 &&
    campaign.testing.is_enabled
  )
}

/**
 * Extract the dimensions and segments actually used in a campaign's variant rules,
 * filtered down to only those referenced. Returns dimension objects with their
 * segments pruned to only the ones targeted by the campaign.
 */
export const getCampaignDimensionsAndSegments = (
  campaign: Campaign,
  dimensions: Dimension[]
): Dimension[] => {
  const usedSegmentsByDimension = new Map<string, Set<string>>()

  campaign.variants?.forEach((variant) => {
    const processRules = (rules: any[]) => {
      rules.forEach((rule) => {
        if (rule.dimensionId && rule.segment) {
          if (!usedSegmentsByDimension.has(rule.dimensionId)) {
            usedSegmentsByDimension.set(rule.dimensionId, new Set())
          }
          usedSegmentsByDimension.get(rule.dimensionId)!.add(rule.segment)
        }
      })
    }

    if (variant.rules?.all) processRules(variant.rules.all)
    if (variant.rules?.any) processRules(variant.rules.any)
  })

  return dimensions
    .filter((d) => usedSegmentsByDimension.has(d.id))
    .map((dimension) => ({
      ...dimension,
      segments: dimension.segments.filter((seg) =>
        usedSegmentsByDimension.get(dimension.id)?.has(seg.id)
      )
    }))
    .filter((d) => d.segments.length > 0)
}

/**
 * Compute the total number of unique personalized variants a campaign produces.
 * This is the cartesian product of segments across all dimensions used in variant rules.
 * E.g. 2 industries × 3 roles = 6 unique variants.
 * Returns 0 if no dimension-based rules exist.
 */
export const getCampaignUniqueVariantCount = (
  campaign: Campaign,
  dimensions: Dimension[]
): number => {
  const filtered = getCampaignDimensionsAndSegments(campaign, dimensions)
  if (filtered.length === 0) return 0

  return filtered.reduce((product, dim) => product * dim.segments.length, 1)
}
